const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const urlBase64ToUint8Array = (value) => {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
};

export const pushSupported = () =>
  typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

export const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

export const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

export const getPushState = async () => {
  if (!pushSupported()) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) return "subscribed";
  } catch (_) {}
  return Notification.permission === "granted" ? "granted" : "default";
};

const getCoords = () => new Promise((resolve) => {
  if (!navigator.geolocation) return resolve(null);
  navigator.geolocation.getCurrentPosition(
    (position) => resolve({ latitude: position.coords.latitude, longitude: position.coords.longitude }),
    () => resolve(null),
    { timeout: 8000, maximumAge: 600000 },
  );
});

export const enablePush = async () => {
  if (!pushSupported()) throw new Error("This browser does not support push alerts.");
  if (isIos() && !isStandalone()) throw new Error("On iPhone: tap Share → Add to Home Screen, open Storm Desk from the icon, then enable alerts.");

  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Notifications were not allowed.");

  const keyResponse = await fetch(`${API}/push/public-key`);
  const { public_key: publicKey } = await keyResponse.json();
  if (!publicKey) throw new Error("Push is not configured on the server.");

  const registration = await navigator.serviceWorker.ready;
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  const coords = await getCoords();
  const response = await fetch(`${API}/push/subscribe`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...subscription.toJSON(), ...(coords || {}) }),
  });
  if (!response.ok) throw new Error("Could not register for alerts.");
  return response.json();
};

export const disablePush = async () => {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return { ok: true };
  await fetch(`${API}/push/unsubscribe`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ endpoint: subscription.endpoint }),
  });
  await subscription.unsubscribe();
  return { ok: true };
};
